;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import { jwtDecode } from 'jwt-decode';
import type { JwtOrganisation, JwtPayload } from '../types/auth';

/**
 * Deep links (e.g. from emails) can carry `?org=<id>&role=<ROLE_CONTEXT>` so the app opens the
 * page in the right company and role. The proxy detects these params, switches the active
 * organisation through `SWITCH_CONTEXT_API_PATH`, and redirects to the clean URL.
 */
export const ORGANISATION_CONTEXT_ORG_PARAM = 'org';
export const ORGANISATION_CONTEXT_ROLE_PARAM = 'role';
export const SWITCH_CONTEXT_API_PATH = '/api/auth/switch-context';
export const SWITCH_CONTEXT_TARGET_PARAM = 'to';

/** Skip switching when the access token is about to expire; the refresh flow reloads the same URL. */
const MIN_ACCESS_TOKEN_SECONDS_LEFT = 30;

export const ORGANISATION_ROLE_CONTEXTS = [
  'EMPLOYER',
  'EMPLOYEE',
  'CONTRACTOR',
  'GIG_WORKER',
] as const;

export type OrganisationRoleContext = (typeof ORGANISATION_ROLE_CONTEXTS)[number];

export interface OrganisationContextParams {
  organisationId: number;
  roleContext?: OrganisationRoleContext;
}

export type OrganisationContextRequestResolution =
  | { action: 'none' }
  /** Already in the requested context: only remove the params. */
  | { action: 'strip'; location: string }
  /** Different context: switch first, then land on the clean target path. */
  | { action: 'switch'; location: string };

function isRoleContext(value: string): value is OrganisationRoleContext {
  return (ORGANISATION_ROLE_CONTEXTS as readonly string[]).includes(value);
}

export function parseOrganisationContextParams(
  searchParams: URLSearchParams
): OrganisationContextParams | null {
  const rawOrganisationId = searchParams.get(ORGANISATION_CONTEXT_ORG_PARAM);
  if (!rawOrganisationId || !/^\d+$/.test(rawOrganisationId)) return null;

  const organisationId = Number(rawOrganisationId);
  if (organisationId <= 0) return null;

  const rawRole = searchParams.get(ORGANISATION_CONTEXT_ROLE_PARAM)?.toUpperCase();

  return {
    organisationId,
    ...(rawRole && isRoleContext(rawRole) ? { roleContext: rawRole } : {}),
  };
}

/** Pathname and query of the URL without the organisation context params. */
export function stripOrganisationContextParams(url: URL): string {
  const searchParams = new URLSearchParams(url.search);
  searchParams.delete(ORGANISATION_CONTEXT_ORG_PARAM);
  searchParams.delete(ORGANISATION_CONTEXT_ROLE_PARAM);

  const search = searchParams.toString();
  return search ? `${url.pathname}?${search}` : url.pathname;
}

/** Only same-origin relative paths are allowed as redirect targets (no open redirects). */
export function getSafeRedirectPath(path: string | null | undefined): string {
  if (!path?.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return '/';
  return path;
}

function decodeUsableAccessToken(accessToken: string | undefined): JwtPayload | null {
  if (!accessToken) return null;

  try {
    const decodedToken = jwtDecode<JwtPayload>(accessToken);
    const secondsLeft = decodedToken.exp - Math.floor(Date.now() / 1000);
    return secondsLeft >= MIN_ACCESS_TOKEN_SECONDS_LEFT ? decodedToken : null;
  } catch {
    return null;
  }
}

function isActiveContextRequested(
  active: { organisationId: number | undefined; roleContext: string | null | undefined },
  params: OrganisationContextParams
): boolean {
  if (active.organisationId !== params.organisationId) return false;
  if (!params.roleContext) return true;

  return active.roleContext?.toUpperCase() === params.roleContext;
}

function isRequestedContextActive(
  decodedToken: JwtPayload,
  params: OrganisationContextParams
): boolean {
  return isActiveContextRequested(
    {
      organisationId: decodedToken.active_organisation?.id ?? decodedToken.organisations?.[0]?.id,
      roleContext: decodedToken.active_organisation?.role_context ?? decodedToken.accessible_app,
    },
    params
  );
}

/** Organisation / role fields the BE adds to each item of `GET /notifications`. */
export interface NotificationContextFields {
  organisation_id?: number | null;
  /** Role context to act in, e.g. `EMPLOYEE`. */
  role?: string | null;
}

/**
 * Context to switch to before opening a notification, or `null` when none is needed: older
 * notifications have no organisation / role, and the user may already be in that context.
 * `role_category` is only a label and never drives the switch.
 */
export function getNotificationContextSwitchTarget(
  notification: NotificationContextFields,
  activeOrganisation: Pick<JwtOrganisation, 'id' | 'role_context'> | null | undefined,
  accessibleApp: string | null | undefined
): OrganisationContextParams | null {
  const { organisation_id: organisationId, role } = notification;
  if (organisationId == null || !role) return null;

  const roleContext = role.toUpperCase();
  if (!isRoleContext(roleContext)) return null;

  const target: OrganisationContextParams = { organisationId, roleContext };

  const isActive = isActiveContextRequested(
    {
      organisationId: activeOrganisation?.id,
      roleContext: activeOrganisation?.role_context ?? accessibleApp,
    },
    target
  );

  return isActive ? null : target;
}

/**
 * Users who can switch role contexts only get expenses while acting as EMPLOYER.
 * Users without a context switcher are unaffected.
 */
export function isExpensesBlockedByRoleContext(
  activeOrganisation:
    | Pick<JwtOrganisation, 'role_context' | 'show_context_switcher'>
    | null
    | undefined
): boolean {
  return (
    activeOrganisation?.show_context_switcher === true &&
    activeOrganisation.role_context?.toUpperCase() !== 'EMPLOYER'
  );
}

/**
 * Decides what the proxy should do with a request that may carry `org` / `role` params.
 * Pure (no I/O) so it can run in any app's `proxy.ts`.
 */
export function resolveOrganisationContextRequest(
  requestUrl: URL,
  accessToken: string | undefined
): OrganisationContextRequestResolution {
  const params = parseOrganisationContextParams(requestUrl.searchParams);
  if (!params) return { action: 'none' };

  const decodedToken = decodeUsableAccessToken(accessToken);
  if (!decodedToken) return { action: 'none' };

  const cleanPath = stripOrganisationContextParams(requestUrl);

  if (isRequestedContextActive(decodedToken, params)) {
    return { action: 'strip', location: cleanPath };
  }

  const switchUrl = new URLSearchParams({
    [ORGANISATION_CONTEXT_ORG_PARAM]: String(params.organisationId),
    ...(params.roleContext ? { [ORGANISATION_CONTEXT_ROLE_PARAM]: params.roleContext } : {}),
    [SWITCH_CONTEXT_TARGET_PARAM]: cleanPath,
  });

  return { action: 'switch', location: `${SWITCH_CONTEXT_API_PATH}?${switchUrl.toString()}` };
}

/** Token-level variant for `proxy.ts`; decodes without any I/O. Unreadable tokens never block. */
export function isExpensesBlockedForAccessToken(accessToken: string | undefined): boolean {
  if (!accessToken) return false;

  try {
    return isExpensesBlockedByRoleContext(jwtDecode<JwtPayload>(accessToken).active_organisation);
  } catch {
    return false;
  }
}
